import React, { useState, useEffect } from "react";
import UserLayout from "../../../../components/UserLayout";
import { useAuth } from "../../../../contexts/AuthContext";
import { api } from "../../../../utils/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import { useToast } from "../../../../contexts/ToastContext";
import Modal from "../../../../components/ui/modal";

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const { showToast } = useToast();

  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    province: "",
    address: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  /* Load user into form */
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        country: user.country || "",
        province: user.province || "",
        address: user.address || "",
      });

      // Check if profile is complete
      const isComplete =
        user.name && user.phone && user.country && user.address;
      setIsProfileCompleted(isComplete);

      if (!isComplete) {
        setShowCompleteProfileModal(true);
        setIsEditingProfile(true);
      }
    }
  }, [user]);

  const UserCounties = user.profile_countries;
  console.log("Countries List", UserCounties);

  /* Validation */
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateProfile = () => {
    const errors = {};
    let valid = true;

    if (!profileData.name.trim()) {
      errors.name = "Full name is required";
      valid = false;
    }

    if (!profileData.email.trim()) {
      errors.email = "Email is required";
      valid = false;
    } else if (!validateEmail(profileData.email)) {
      errors.email = "Invalid email address";
      valid = false;
    }

    if (!profileData.phone.trim()) {
      errors.phone = "Phone number is required";
      valid = false;
    }

    if (!profileData.country) {
      errors.country = "Country is required";
      valid = false;
    }

    if (!profileData.province.trim()) {
      errors.province = "Province/State is required";
      valid = false;
    }

    if (!profileData.address.trim()) {
      errors.address = "Address is required";
      valid = false;
    }

    setProfileErrors(errors);
    return valid;
  };

  const validatePassword = () => {
    const errors = {};
    let valid = true;

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
      valid = false;
    }

    if (!passwordData.newPassword || passwordData.newPassword.length < 8) {
      errors.newPassword = "Minimum 8 characters required";
      valid = false;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      valid = false;
    }

    setPasswordErrors(errors);
    return valid;
  };

  /* Update Profile */
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;

    setLoading(true);
    try {
      const res = await api.updateProfile(profileData);
      const updatedUser = res.data.user;
      updateUser(updatedUser);

      if (updatedUser.is_approved === "10") {
        setShowPendingModal(true);
      } else {
        showToast("Profile updated successfully", "success");
        setIsProfileCompleted(true);
        setIsEditingProfile(false);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || "Profile update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* Change Password */
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setLoading(true);
    try {
      await api.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      showToast("Password changed successfully", "success");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
    } catch (err) {
      showToast(
        err.response?.data?.detail || "Password change failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Format country name - replace underscores with spaces
  const formatCountryName = (country) => {
    if (!country) return "";
    return country.replace(/_/g, " ");
  };

  return (
    <UserLayout>
      <div className="flex gap-8">
        {/* Right Content - Forms */}
        <div className="flex-1 min-w-0">
          <div className="mb-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2">
              Profile Settings
            </h4>
            <p className="text-gray-500">
              Manage your personal information and security preferences
            </p>
          </div>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="inline-flex h-11 items-center justify-start rounded-lg bg-gray-100 p-1 mb-6">
              <TabsTrigger
                value="profile"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Profile Information
              </TabsTrigger>
              <TabsTrigger
                value="password"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                Change Password
              </TabsTrigger>
            </TabsList>

            {/* Profile Information Tab */}
            <TabsContent value="profile">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Personal Information
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {isProfileCompleted && !isEditingProfile
                        ? "Your profile information"
                        : "Update your personal details and contact information"}
                    </p>
                  </div>

                  {/* Edit Button - Only show when profile is completed and not editing */}
                  {isProfileCompleted && !isEditingProfile && (
                    <Button
                      onClick={() => setIsEditingProfile(true)}
                      variant="outline"
                      className="h-9 px-4 flex items-center gap-2 hover:bg-gray-100"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Profile
                    </Button>
                  )}
                </div>

                <div className="p-6">
                  {isProfileCompleted && !isEditingProfile ? (
                    /* View Mode - Display Only */
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Full Name
                          </p>
                          <p className="text-sm text-gray-900">{user?.name}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Email Address
                          </p>
                          <p className="text-sm text-gray-900">{user?.email}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Phone Number
                          </p>
                          <p className="text-sm text-gray-900">{user?.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Country
                          </p>
                          <p className="text-sm text-gray-900">
                            {formatCountryName(user?.country)}
                          </p>
                        </div>
                      </div>

                      <div className="pb-4 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Province / State
                        </p>
                        <p className="text-sm text-gray-900">
                          {user?.province}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Address
                        </p>
                        <p className="text-sm text-gray-900">{user?.address}</p>
                      </div>
                    </div>
                  ) : (
                    /* Edit Mode - Form with Disabled Country & State */
                    <div className="space-y-5 max-w-md">
                      {/* Full Name */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Full Name
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={profileData.name}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              name: e.target.value,
                            })
                          }
                          className="h-10"
                          placeholder="Enter your full name"
                        />
                        {profileErrors.name && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.name}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Email Address
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="email"
                          value={profileData.email}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              email: e.target.value,
                            })
                          }
                          className="h-10"
                          placeholder="your.email@example.com"
                        />
                        {profileErrors.email && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.email}
                          </p>
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Phone Number
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              phone: e.target.value,
                            })
                          }
                          className="h-10"
                          placeholder="+1 (555) 123-4567"
                        />
                        {profileErrors.phone && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.phone}
                          </p>
                        )}
                      </div>

                      {/* Country - DISABLED */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Country
                          <span className="text-red-500">*</span>

                        </Label>
                        {/* <select
                          className="w-full h-10 rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-100 cursor-not-allowed opacity-60"
                          value={profileData.country}
                          disabled={isProfileCompleted}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              country: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Country</option>
                          <option value="Thailand">Thailand</option>
                          <option value="South_Korea">South Korea</option>
                          <option value="India">India</option>
                        </select> */}
                        <select
                          className={`w-full h-10 rounded-md border border-gray-300 px-3 py-2 text-sm ${isProfileCompleted
                              ? "bg-gray-100 cursor-not-allowed opacity-60"
                              : "bg-white"
                            }`}
                          value={profileData.country}
                          disabled={isProfileCompleted}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              country: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Country</option>
                          <option value="Thailand">Thailand</option>
                          <option value="South_Korea">South Korea</option>
                          <option value="India">India</option>
                        </select>
                        {!isProfileCompleted && profileErrors.country && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.country}
                          </p>
                        )}
                      </div>

                      {/* Province - DISABLED */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Province / State
                          <span className="text-red-500">*</span>

                        </Label>
                        <Input
                          value={profileData.province}
                          disabled={isProfileCompleted}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              province: e.target.value,
                            })
                          }
                          className={`h-10 ${isProfileCompleted ? "bg-gray-100 cursor-not-allowed opacity-60" : ""}`}
                          placeholder="Enter your province or state"
                        />
                        {!isProfileCompleted && profileErrors.province && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.province}
                          </p>
                        )}
                      </div>

                      {/* Address */}
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                          Address
                          <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          value={profileData.address}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              address: e.target.value,
                            })
                          }
                          className="min-h-[80px] resize-none text-sm"
                          placeholder="Enter your full address"
                        />
                        {profileErrors.address && (
                          <p className="text-xs text-red-500 mt-1">
                            {profileErrors.address}
                          </p>
                        )}
                      </div>

                      <div className="pt-6 border-t border-gray-200 flex items-center justify-end gap-3">
                        {isProfileCompleted && (
                          <Button
                            type="button"
                            variant="outline"
                            className="h-10 px-5"
                            onClick={() => {
                              setProfileData({
                                name: user?.name || "",
                                email: user?.email || "",
                                phone: user?.phone || "",
                                country: user?.country || "",
                                province: user?.province || "",
                                address: user?.address || "",
                              });
                              setProfileErrors({});
                              setIsEditingProfile(false);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          onClick={handleProfileUpdate}
                          disabled={loading}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white h-10 px-6 font-medium shadow-sm"
                        >
                          {loading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Updating...
                            </>
                          ) : (
                            "Save Changes"
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Change Password Tab */}
            <TabsContent value="password">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-base font-semibold text-gray-900">
                    Change Password
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Keep your account secure with a strong password
                  </p>
                </div>

                <div className="p-6">
                  <div className="space-y-5 max-w-md">
                    {/* Current Password */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        Current Password
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              currentPassword: e.target.value,
                            })
                          }
                          className="h-10 pr-10"
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                      {passwordErrors.currentPassword && (
                        <p className="text-xs text-red-500 mt-1">
                          {passwordErrors.currentPassword}
                        </p>
                      )}
                    </div>

                    {/* New Password */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        New Password
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPassword: e.target.value,
                            })
                          }
                          className="h-10 pr-10"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                      {passwordErrors.newPassword && (
                        <p className="text-xs text-red-500 mt-1">
                          {passwordErrors.newPassword}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        Confirm New Password
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              confirmPassword: e.target.value,
                            })
                          }
                          className="h-10 pr-10"
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                      {passwordErrors.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">
                          {passwordErrors.confirmPassword}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 px-5"
                      onClick={() => {
                        setPasswordData({
                          currentPassword: "",
                          newPassword: "",
                          confirmPassword: "",
                        });
                        setPasswordErrors({});
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePasswordChange}
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white h-10 px-6 font-medium shadow-sm"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Changing...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Modals */}
      <Modal
        open={showCompleteProfileModal}
        title="Complete Your Profile"
        message="Please complete your profile to unlock our smart India e-Visa processing services including document-based data extraction and multi-language support."
        onClose={() => setShowCompleteProfileModal(false)}
      />

      <Modal
        open={showPendingModal}
        title="Profile Submitted"
        message="Your profile has been sent for approval. You will gain full access once approved."
        onClose={() => {
          setShowPendingModal(false);
          logout();
        }}
      />
    </UserLayout>
  );
}
